import { useCallback, useMemo } from 'react'
import cn from 'classnames'

import { Icon, IconName } from '@/icons'
import { DropDown } from '@/components/DropDown'
import { ITab } from '@/components/Tabs'

import styles from './Select.module.scss'

interface SelectProps<TValue> {
  className?: string
  items: ITab<TValue>[]
  value: TValue
  onChange: (value: TValue) => void
}

export const Select = <TValue,>({
  className,
  items,
  value,
  onChange,
}: SelectProps<TValue>) => {
  const activeItem = useMemo(() => {
    return items.find((i) => i.value === value) ?? items[0]
  }, [items, value])

  const preparedItems = useMemo(() => {
    return items.filter((item) => item.value !== value).reverse()
  }, [items, value])

  if (items.length === 0) {
    return null
  }

  return (
    <DropDown
      className={cn(styles.container, className)}
      buttonClassName={styles.button}
      buttonContent={
        <span className={styles.buttonContent}>
          {activeItem.icon != null ? (
            <span className={styles.iconButtonContainer}>
              <Icon name={activeItem.icon} className={styles.iconButton} />
            </span>
          ) : null}

          {activeItem.title}
        </span>
      }
    >
      <div className={cn(styles.list)}>
        {preparedItems.map((item, index, array) => (
          <Option
            key={item.title}
            icon={item.icon}
            value={item.value}
            title={item.title}
            isLast={array.length === index + 1}
            onChange={onChange}
          />
        ))}
      </div>
    </DropDown>
  )
}

interface Option<TValue> {
  icon?: IconName
  value: TValue
  title: string
  isLast: boolean
  onChange: (value: TValue) => void
}
const Option = <TValue,>({
  icon,
  value,
  onChange,
  title,
  isLast,
}: Option<TValue>) => {
  const click = useCallback(() => {
    onChange(value)
  }, [onChange, value])
  return (
    <button
      type="button"
      className={cn(styles.option, {
        [styles.withoutBorder]: isLast,
      })}
      onClick={click}
    >
      <span className={styles.iconContainer}>
        {icon != null ? <Icon name={icon} className={styles.icon} /> : null}
      </span>
      {title}
    </button>
  )
}
